import React, { PureComponent } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import classNames from 'classnames';

import TitleBar from 'widgets/Bar/TitleBar';
import ActionBar from 'widgets/Bar/ActionBar';

export default class Modal extends PureComponent {
  static propTypes = {
    actions: PropTypes.node,
    actionsFlexType: PropTypes.string,
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
    closeOnOuterClick: PropTypes.bool,
    desktopLargeSize: PropTypes.bool,
    forceRenderCloseButton: PropTypes.bool,
    mainViewScrollable: PropTypes.bool,
    mobileFullWindow: PropTypes.bool,
    onCancel: PropTypes.func.isRequired,
    title: PropTypes.node,
  };

  static defaultProps = {
    closeOnOuterClick: true,
    mobileFullWindow: true,
  };

  componentDidMount() {
    window.addEventListener('keydown', this.handleCloseOnKeyDown, false);
    let reactViewNode = document.getElementById('react-view');
    if (reactViewNode) {
      reactViewNode.setAttribute('aria-hidden', 'true');
      reactViewNode = null;
    }
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleCloseOnKeyDown, false);
    let reactViewNode = document.getElementById('react-view');
    if (reactViewNode) {
      reactViewNode.removeAttribute('aria-hidden');
      reactViewNode = null;
    }
  }

  close = () => {
    const { onCancel } = this.props;

    if (typeof onCancel === 'function') {
      onCancel();
    }
  };

  handleInnerModalClick = (e) => {
    e.stopPropagation();
  };

  handleCloseOnKeyDown = (event) => {
    const { key } = event;

    if (this.props.children && key === 'Escape') {
      this.close();
    }
  };

  handleCloseOnOuterClick = () => {
    if (this.props.closeOnOuterClick) {
      this.close();
    }
  };

  renderTitle(forceRender) {
    if (!forceRender && !this.props.title) {
      return null;
    }

    // ID is required for `aria-labelledby`
    return (
      <TitleBar
        className="gn-modal__topBar"
        close={this.close}
      >
        <span id="gn-modal__title">{this.props.title}</span>
      </TitleBar>
    );
  }

  renderActions() {
    if (!this.props.actions) {
      return null;
    }

    return (
      <ActionBar
        className="gn-modal__bottomBar"
        flexType={this.props.actionsFlexType}
      >
        {this.props.actions}
      </ActionBar>
    );
  }

  render() {
    const {
      children,
      className: extraClassName,
      desktopLargeSize,
      forceRenderCloseButton,
      mainViewScrollable,
      mobileFullWindow,
    } = this.props;

    /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
    /* eslint-disable jsx-a11y/click-events-have-key-events */
    return createPortal(
      <>
        {!mainViewScrollable &&
          <Helmet>
            {/* It has to be done as an attribute as React Helmet doesn't support css class appending */}
            <html data-modal-visible="true" />{/* eslint-disable-line jsx-a11y/html-has-lang */}
          </Helmet>
        }
        <div
          className={classNames('gn-modal__overlay', {
            'gn-modal__overlay--mobileFullWindow': mobileFullWindow,
          })}
          role="presentation"
          tabIndex="-1"
        />
        <div
          aria-labelledby="gn-modal__title"
          className={classNames('gn-modal', {
            'gn-modal--desktopLargeSize': desktopLargeSize,
            'gn-modal--mobileFullWindow': mobileFullWindow,
          })}
          onClick={this.handleCloseOnOuterClick}
          role="dialog"
          tabIndex="-1"
        >
          <div
            className={classNames('gn-modal__dialog', extraClassName, {
              'gn-modal__dialog--mobileFullWindow': mobileFullWindow,
            })}
          >
            <div
              className={classNames('gn-modal__wrapper', 'gn-content', {
                'gn-elevated--z5': !mobileFullWindow,
                'gn-elevated--z5@md': mobileFullWindow,
                'gn-modal__wrapper--mobileFullWindow': mobileFullWindow,
              })}
              onClick={this.handleInnerModalClick}
              role="document"
              tabIndex="-1"
            >
              {this.renderTitle(forceRenderCloseButton)}
              <div className="gn-modal__content">
                {children}
              </div>
              {this.renderActions()}
            </div>
          </div>
        </div>
      </>,
      document.getElementById('modal-root')
    );
  }
}
